// Angular
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {KeycloakService} from '@wlp/ui-bs-login';
import {CryptoService, StorageService} from '@wlp/ui-bs-oauth';
import { JweDto } from '@wlp/ui-bs-oauth/lib/dto/jwe.dto';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import {environment} from '../../../environments/environment';

interface ICryptoData {
  publicKeyFromBack: any;
  checksumKeyFromBack: any;
  uuidAccount: any;
  privateFrontKey: any;
}

/**
 * More information there => https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
 */
@Injectable()
export class InterceptService implements HttpInterceptor {

  private clientId = environment.keycloakConfig.clientId;
  private clientVersion = environment.appVersion;
  private realm = environment.keycloakConfig.realm;
  private wlID = environment.wlp;
  private readonly urlsWithoutEncryption: string[] = [
    '/spe-view-n-account/v1/access/accounts',
    '/spe-n-accounts/v1/certificates/check-web',
    '/spe-j-files/api/html-to-pdf',
    '/spe-j-cash-in/api/billets/download/transaction-id/',
    '/spe-j-cnab/api/400/process/download',
    '/spe-j-cnab/api/240/process/download',
    '/spe-j-cnab/api/240/extract/download/',
    '/spe-view-n-statament/v1/download/',
    '/download', // Tratar o download do Cnab 240 e 400
  ];

  constructor(
    private keycloak: KeycloakService, private cryptoService: CryptoService, private storageService: StorageService,
  ) {}

  /**
   * Checks if the url is excluded from having the Bearer Authorization
   * header added.
   *
   * @param req http request from @angular http module.
   * @param excludedUrlRegex contains the url pattern and the http methods,
   * excluded from adding the bearer at the Http Request.
   */
  private isUrlExcluded(
    {method, url}: HttpRequest<any>,
    {urlPattern, httpMethods}: ExcludedUrlRegex,
  ): boolean {
    const httpTest =
      httpMethods.length === 0 ||
      httpMethods.join().indexOf(method.toUpperCase()) > -1;

    const urlTest = urlPattern.test(url);

    return httpTest && urlTest;
  }

  /**
   * Intercept implementation that checks if the request url matches the excludedUrls.
   * If not, adds the Authorization header to the request if the user is logged in.
   *
   * param req
   * param next
   */

  // intercept request and add token
  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const { enableBearerInterceptor, excludedUrls } = this.keycloak;
    const isQA = environment.qa;
    const isProd = environment.production;
    const isDev = !isQA && !isProd;

    // Verifica se o interceptor está desabilitado
    if (!enableBearerInterceptor) {
      return next.handle(request);
    }

    // Verifica se a URL está na lista de exclusão
    const shallPass: boolean = excludedUrls.findIndex( (item) => this.isUrlExcluded(request, item)) > -1;
    if (shallPass) {
      return next.handle(request);
    }

    // Adiciona headers padrão
    request = request.clone({
      headers: request.headers
        .set('Client-Id', this.clientId)
        .set('X-Client-Version', this.clientVersion)
        .set('X-Realm', this.realm)
        .set('X-WL-ID', this.wlID)
        .set('Content-type', 'application/json'),
    });

    if (isDev) {
      return this.handleAuthFlow(request, next);
    } else {
      return from(this.fetchAllKeysKey()).pipe(
        switchMap((cryptoInfo) => {
          return from(this.getDeviceInfo()).pipe(
            switchMap((device) => {
              if (device && device.pbcKeyChecksum) {
                return of(request.clone({ headers: request.headers.set('w-d-id', device.pbcKeyChecksum) }));
              }
              return of(request);
            }),
            switchMap((requestWithHeaders) => this.cryptRequest(requestWithHeaders, cryptoInfo)),
            switchMap((encryptedRequest) => this.handleAuthFlow(encryptedRequest, next)),
            switchMap((event) => this.handleCryptedResponse(event, cryptoInfo)),
            catchError((error) => throwError(error)),
          );
        }),
      );
    }
  }

  private getDeviceInfo(): Promise<any> {
    return this.storageService.get(environment.jwe.cs, true);
  }

  private cryptRequest(request: HttpRequest<any>, cryptoInfo: ICryptoData): Observable<HttpRequest<any>> {
    if (this.shouldSkipEncryption(request)) { return of(request); }
    const xBacon = 'X-BACON'; // Variavel para verificar se o request.body já foi criptografado, devido problema de loop

    // NÃO RETIRAR ESTÁ VALIDAÇÃO ATÉ RESOLVER O PROBLEMA DE LOOP
    if (request.headers.get(xBacon)) { return of(request); }

    const jweDto: JweDto = {
      publicKey: cryptoInfo.publicKeyFromBack, // public key do back
      checksumKeys: cryptoInfo.checksumKeyFromBack,
      uuidAccount: cryptoInfo.uuidAccount,
    };

    return from(this.cryptoService.createJWE(request.body, cryptoInfo.publicKeyFromBack, jweDto)).pipe(
      map((encryptedBody) => request.clone({
        body: this.buildJWEString(encryptedBody),
        headers: request.headers.set(xBacon, 'PLUS'),
      })),
    );
  }

  private shouldSkipEncryption(request: HttpRequest<any>): boolean {
    return (!request.body || this.urlsWithoutEncryption.some( (url) => request.url.includes(url)));
  }

  private shouldSkipDecryption(urlRequest: string): boolean {
    return (!urlRequest || this.urlsWithoutEncryption.some( (url) => urlRequest.includes(url)));
  }

  private buildJWEString(encryptedBody): string {
    const protectedHeader = encryptedBody.protected;
    const encryptedKey = encryptedBody.recipients[0].encrypted_key;
    const iv = encryptedBody.iv;
    const ciphertext = encryptedBody.ciphertext;
    const tag = encryptedBody.tag;

    return `${protectedHeader}.${encryptedKey}.${iv}.${ciphertext}.${tag}`;
  }

  private handleCryptedResponse(event: HttpEvent<any>, cryptoInfo: ICryptoData): Observable<HttpEvent<any>> {
    if (event instanceof HttpResponse && event.body && event.body.content) {
      if (this.shouldSkipDecryption(event.url)) {
        return of(event);
      } else {
        return from(this.cryptoService.decryptJWE(event.body.content, cryptoInfo.privateFrontKey)).pipe(
          map((decrypt) => event.clone({ body: JSON.parse(decrypt) })),
          catchError((error) => throwError(error)),
        );
      }
    }
    return of(event);
  }

  private async fetchAllKeysKey(): Promise<ICryptoData> {
    try {
      const publicKeyFromBack = await this.storageService.get(`${environment.jwe.pub}`, true);
      const checksumKeyFromBack = await this.storageService.get(`${environment.jwe.csprvt}`, true);
      const uuidAccount = await this.storageService.get(`${environment.jwe.ua}`, true);
      const privateFrontKey = await this.storageService.get(`${environment.jwe.pv}`, true);

      // return await Promise.all([
      //   publicKeyFromBackPromise,
      //   checksumKeyFromBackPromise,
      //   uuidAccountPromise,
      //   privateFrontKeyPromise
      // ]) as unknown as Promise<ICryptoData>

      return { publicKeyFromBack, checksumKeyFromBack, uuidAccount, privateFrontKey };
    } catch (error) {
      return error;
    }
  }

  // Método auxiliar para lidar com o fluxo de autenticação
  private handleAuthFlow(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.keycloak.isLoggedIn()).pipe(
      mergeMap((loggedIn: boolean) => loggedIn ? this.handleRequestWithTokenHeader(request, next) : next.handle(request)),
    );
  }

  /**
   * Adds the token of the current user to the Authorization header
   *
   * param req
   * param next
   */
  private handleRequestWithTokenHeader(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    return from(this.keycloak.updateToken(4)).pipe(
      switchMap(() => {
        return this.keycloak.addTokenToHeader(req.headers).pipe(
          mergeMap((headersWithBearer) => {
            const kcReq = req.clone({headers: headersWithBearer});
            return next.handle(kcReq);
          }),
        );
      }),
    );
  }
}

/**
 * Similar to ExcludedUrl, contains the HTTP methods and a regex to
 * include the url patterns.
 * This interface is used internally by the KeycloakService.
 */
export interface ExcludedUrlRegex {
  urlPattern: RegExp;
  httpMethods?: HttpMethods[];
}

/**
 * HTTP Methods
 */
export type HttpMethods =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'DELETE'
  | 'OPTIONS'
  | 'HEAD'
  | 'PATCH';
